import { forwardRef, type ReactNode, useCallback, useMemo } from 'react';
import { createGlobalStyle, css, StyleSheetManager } from 'styled-components';
import type {
  AutoSizeValue,
  FontValue
} from '@elfsight/configurator-contracts';
import { useMountingInventoryAPI } from '@elfsight/widget-mounting-inventory';
import { Shadow, ShadowGlobalPropsProvider } from '@elfsight/widget-shadow';
import { parseWidth } from '@elfsight/widget-utils';
import { parseCustomCSS } from '../custom-css/parsed-custom-css';

export interface ShadowEmbedRootProps {
  width?: string | number | AutoSizeValue;
  fullWidth?: boolean;
  font?: FontValue;
  textColor?: string;
  defaultCSS?: string;
  customCSS?: string;
  children?: ReactNode;
  customCSSClassName?: string;
}

const IS_DEV = process.env.NODE_ENV === 'development';

export const ShadowEmbedRoot = forwardRef<HTMLDivElement, ShadowEmbedRootProps>(
  function ShadowEmbedRoot(
    {
      width = 'auto',
      fullWidth = true,
      font,
      textColor,
      defaultCSS,
      customCSS,
      children,
      customCSSClassName
    }: ShadowEmbedRootProps,
    ref
  ) {
    const mountingInventoryAPI = useMountingInventoryAPI();

    const parsedCustomCSS = useMemo(() => {
      if (!customCSS) {
        return undefined;
      }

      return parseCustomCSS(customCSS, {
        widgetRootSelector: mountingInventoryAPI?.getWidgetRootSelector()
      });
    }, [customCSS, mountingInventoryAPI]);

    const wrap = useCallback(
      (root: ShadowRoot, children: ReactNode) => {
        return (
          <StyleSheetManager
            target={root}
            shouldForwardProp={(propName) => !propName.startsWith('_')}
          >
            <HostStyle $font={font} $textColor={textColor} />
            <Styles $css={defaultCSS} />
            <Styles $css={parsedCustomCSS?.internal} />

            {children}
          </StyleSheetManager>
        );
      },
      [font, textColor, defaultCSS, parsedCustomCSS]
    );

    return (
      <ShadowGlobalPropsProvider mode={IS_DEV ? 'open' : 'closed'} wrap={wrap}>
        <Styles $css={parsedCustomCSS?.external} />

        <Shadow ref={ref} className={`es-embed-root ${customCSSClassName}`}>
          <WidgetRootStyle
            $isFullWidth={fullWidth}
            $maxWidth={width}
            $font={font}
          />
          {children}
        </Shadow>
      </ShadowGlobalPropsProvider>
    );
  }
);

const Styles = createGlobalStyle<{
  $css: string | undefined;
}>`${({ $css }) => css`
  ${$css}
`}`;

const HostStyle = createGlobalStyle<{
  $font: FontValue | undefined;
  $textColor: string | undefined;
}>`
  :host {
    -webkit-font-smoothing: antialiased;

    font-family: ${({ $font }) => !!$font?.value && `"${$font.value}"`};
    color: ${({ $textColor }) => $textColor};
  }

  * {
    box-sizing: border-box !important;
    outline: none !important;
  }

  button, input, select, textarea {
    font: inherit;
  }
`;

const WidgetRootStyle = createGlobalStyle<{
  $isFullWidth: boolean;
  $maxWidth: string | number | AutoSizeValue;
  $font: FontValue | undefined;
}>`
  :host {
    position: relative;
    width: ${({ $isFullWidth }) => ($isFullWidth ? '100%' : undefined)};
    max-width: ${({ $maxWidth }) => parseWidth($maxWidth)};
    margin: 0 auto;
  }

  /* применяем шрифт внутри SD для случаев, когда шрифт на хосте в LD может быть перезаписан конфликтом */
  :host > * {
    font-family: ${({ $font }) => !!$font?.value && `"${$font.value}"`};
  }
`;
